import { Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import TrustBadge, { TIER_CONFIG, getTierPerks } from '@/components/TrustBadge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface TrustInfoModalProps {
  trigger?: React.ReactNode;
  className?: string;
}

export default function TrustInfoModal({ trigger, className }: TrustInfoModalProps) {
  const tiers: Array<'newbie' | 'reliable' | 'trusted' | 'elite'> = ['newbie', 'reliable', 'trusted', 'elite'];

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className={cn("h-6 w-6", className)} data-testid="button-trust-info">
            <Info className="w-4 h-4 text-muted-foreground" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90svh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Trust Rating System</DialogTitle>
          <DialogDescription>
            Trust ratings help you identify the most reliable reviewers in your network
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* How It Works */}
          <div>
            <h3 className="font-semibold text-base mb-3">How Trust Ratings Work</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>Trust ratings are composite scores calculated based on:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Trustworthy rating (30%)</li>
                <li>Review helpfulness (30%)</li>
                <li>Review activity (25%)</li>
                <li>Social influence (15%)</li>
              </ul>
              <p className="mt-3">
                Scores range from <span className="font-semibold text-foreground">0-100</span>, with higher scores indicating more trustworthy reviewers.
              </p>
            </div>
          </div>

          {/* Trust Tiers */}
          <div>
            <h3 className="font-semibold text-base mb-3">Trust Tiers</h3>
            <div className="space-y-3">
              {tiers.map((tier) => {
                const config = TIER_CONFIG[tier];
                const [min, max] = config.range;
                const exampleScore = min + Math.floor((max - min) / 2);
                const perks = getTierPerks(tier);

                return (
                  <Card key={tier} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <TrustBadge
                          trustScore={exampleScore}
                          trustTier={tier}
                          size="lg"
                          showScore={true}
                          data-testid={`trust-info-badge-${tier}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className={cn("font-bold text-base", config.color)}>{config.label}</h4>
                            <span className="text-xs text-muted-foreground">
                              ({min}-{max} points)
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{config.description}</p>
                        </div>
                      </div>
                      
                      {/* Perks */}
                      <div className="pl-14">
                        <p className="text-xs font-semibold mb-1.5 text-muted-foreground">Benefits:</p>
                        <ul className="space-y-1">
                          {perks.map((perk, index) => (
                            <li key={index} className="text-xs text-foreground flex items-start gap-1.5">
                              <span className={cn("mt-0.5", config.color)}>•</span>
                              <span>{perk}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* How to Improve Trust */}
          <div>
            <h3 className="font-semibold text-base mb-3">How to Improve Your Trust Rating</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Write detailed, honest reviews with photos</li>
                <li>Be consistent and active in the community</li>
                <li>Provide helpful information that others appreciate</li>
                <li>Build your network by following other foodies</li>
                <li>Respond to comments and engage with your audience</li>
              </ul>
            </div>
          </div>

          {/* Trust vs. Helpful Voting */}
          <div>
            <h3 className="font-semibold text-base mb-3">Voting on Reviews</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold mb-1">Helpful Voting</p>
                <p className="text-sm text-muted-foreground">
                  Vote if a specific review was helpful in making your dining decision. This helps surface the best reviews.
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold mb-1">Trust Voting</p>
                <p className="text-sm text-muted-foreground">
                  Vote on whether you trust this reviewer overall. This builds their reputation across all their reviews.
                </p>
              </div>
            </div>
          </div>

          <div className="pt-2">
            <Button className="w-full" variant="outline" data-testid="button-close-trust-info">
              Got it!
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
